import React, { useState, useEffect, forwardRef } from 'react';
import { Picture } from '@ui';
import { useKeenSlider } from 'keen-slider/react';
import ArrowCircle from '@components/Carousel/components/ArrowCircle';
import type { AttachmentsModel } from '@schema/cart';
import type { ShopFragmentMainEndpointFragment } from '@schema/main';
import { oysterBarBenefitsImage } from '@images';
import { useShops2 } from '@customHooks/useShops/useShops';
import { twcls } from '@utils/twcls';
import { flatMapArray } from '@utils/flatMapArray';
import './styles.scss';

const DEFAULT_MOBILE_TITLE_LINES = ['Легко атмосферно', 'без пафоса'];

interface AtmosphereListItem {
  text: string;
  mobileLines?: [string, string];
}

interface BarInteriorPhotoT {
  id: string | number;
  image?: string | null;
  alt: string;
  description: string;
}

const DEFAULT_LIST_ITEMS: AtmosphereListItem[] = [
  { text: 'Уютный дизайн' },
  { text: 'Летние веранды' },
  { text: 'Дружелюбные бармены' }
];

interface EasyAtmosphereProps {
  shopInfo?: ShopFragmentMainEndpointFragment;
  className?: string;
  mobileTitleLines?: string[];
  listItems?: AtmosphereListItem[];
}

const EasyAtmosphere = forwardRef<HTMLElement, EasyAtmosphereProps>(
  (
    {
      shopInfo,
      className,
      mobileTitleLines = DEFAULT_MOBILE_TITLE_LINES,
      listItems = DEFAULT_LIST_ITEMS
    },
    ref
  ) => {
    const { shopsData, imagesData, isLoading } = useShops2();
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isMobile, setIsMobile] = useState(false);
    const [shopImages, setShopImages] = useState<any[]>([]);

    useEffect(() => {
      const checkIsMobile = (): void => {
        setIsMobile(window.innerWidth <= 767);
      };

      checkIsMobile();
      window.addEventListener('resize', checkIsMobile);

      return () => {
        window.removeEventListener('resize', checkIsMobile);
      };
    }, []);

    useEffect(() => {
      if (isLoading || shopsData.length === 0) return;

      if (shopInfo?.id) {
        const currentShop = shopsData.find(shop => shop.id === shopInfo.id);
        if (currentShop?.images_landing?.length) {
          const imageWithTitle = {
            id: currentShop.id,
            attachmentsItems: currentShop.images_landing.map(
              (img: AttachmentsModel) => ({
                description: currentShop.title || 'Фото изнутри наших баров',
                attachmentsItem: {
                  tempFullFilePath: img.tempFullFilePath
                }
              })
            ),
            shopTitle: currentShop.title ?? null
          };
          setShopImages([imageWithTitle]);
        }
      } else {
        const barsWithLandingImages = shopsData
          .filter(shop =>
            shop?.attachments?.some(attachment => attachment.type === 'landing')
          )
          .map(shop => ({
            id: shop.id,
            attachmentsItems:
              shop.attachments
                ?.filter(attachment => attachment.type === 'landing')
                .map(attachment => ({
                  description:
                    attachment.description ||
                    shop.title ||
                    'Фото изнутри наших баров',
                  weight: attachment.weight,
                  attachmentsItem: {
                    tempFullFilePath:
                      attachment.attachmentsItem?.tempFullFilePath
                  }
                })) || [],
            shopTitle: shop.title ?? null
          }));
        setShopImages(barsWithLandingImages);
      }
    }, [shopInfo, shopsData, imagesData, isLoading]);

    const [sliderRef, instanceRef] = useKeenSlider(
      {
        slides: {
          perView: 1,
          spacing: 16
        },
        loop: false,
        mode: 'free-snap',
        slideChanged(slider) {
          setCurrentSlide(slider.track.details.rel);
        }
      },
      isMobile ? [] : undefined
    );

    const nextSlide = (): void => {
      instanceRef.current?.next();
    };

    const prevSlide = (): void => {
      instanceRef.current?.prev();
    };

    const barInteriorPhotos =
      shopImages.length > 0
        ? flatMapArray(shopImages, (item, shopIndex): BarInteriorPhotoT[] => {
            if (shopInfo?.id) {
              return (
                item.attachmentsItems?.map((attachment, imageIndex) => ({
                  id: `${item.id || shopIndex}-${imageIndex}`,
                  image: attachment.attachmentsItem?.tempFullFilePath,
                  alt:
                    item.shopTitle || `${shopInfo?.title || 'Устричный бар'}`,
                  description:
                    attachment.description ||
                    item.shopTitle ||
                    `${shopInfo?.title || 'Устричный бар'}`
                })) || []
              );
            } else {
              const attachmentWithMinWeight = item.attachmentsItems?.reduce(
                (min, current) => {
                  const currentWeight = current.weight || 0;
                  const minWeight = min.weight || 0;
                  return currentWeight < minWeight ? current : min;
                }
              );

              if (attachmentWithMinWeight) {
                return [
                  {
                    id: `${item.id || shopIndex}-0`,
                    image:
                      attachmentWithMinWeight.attachmentsItem?.tempFullFilePath,
                    alt: item.shopTitle || 'Устричный бар',
                    description:
                      attachmentWithMinWeight.description ||
                      item.shopTitle ||
                      'Устричный бар'
                  }
                ];
              }
            }

            return [];
          })
        : [
            {
              id: 1,
              image: oysterBarBenefitsImage,
              alt: 'Бар на Привольной',
              description: 'Бар на Привольной'
            }
          ];

    useEffect(() => {
      if (instanceRef.current && barInteriorPhotos.length > 0) {
        instanceRef.current.update();
      }
    }, [barInteriorPhotos.length, instanceRef]);

    return (
      <section
        ref={ref}
        id='atmosphere'
        className={twcls('easy-atmosphere', className)}
      >
        <div className='easy-atmosphere__container wrapper-container'>
          <div className='easy-atmosphere__content'>
            <h2 className='easy-atmosphere__title oysterbar-test-title-secondary'>
              <span className='easy-atmosphere__title-desktop'>
                <span className='easy-atmosphere__title-line'>Легко</span>
                <span className='easy-atmosphere__title-line'>атмосферно</span>
                <span className='easy-atmosphere__title-line'>без пафоса</span>
              </span>
              <span className='easy-atmosphere__title-mobile'>
                {mobileTitleLines.map(line => (
                  <span key={line} className='easy-atmosphere__title-line'>
                    {line}
                  </span>
                ))}
              </span>
            </h2>

            <ul className='easy-atmosphere__list'>
              {listItems.map(item => (
                <li
                  key={item.text}
                  className={twcls(
                    'easy-atmosphere__list-item',
                    item.mobileLines &&
                      'easy-atmosphere__list-item--split-mobile'
                  )}
                >
                  {item.mobileLines ? (
                    <>
                      <span className='easy-atmosphere__list-item-desktop'>
                        {item.text}
                      </span>
                      <span className='easy-atmosphere__list-item-mobile'>
                        <span className='easy-atmosphere__list-item-line'>
                          {item.mobileLines[0]}
                        </span>
                        <span className='easy-atmosphere__list-item-line'>
                          {item.mobileLines[1]}
                        </span>
                      </span>
                    </>
                  ) : (
                    item.text
                  )}
                </li>
              ))}
            </ul>
          </div>

          <div className='easy-atmosphere__gallery'>
            <div className='easy-atmosphere__gallery-container'>
              {barInteriorPhotos.length > 1 && !isMobile && (
                <div className='arrow-circle-container'>
                  <ArrowCircle
                    left
                    onClick={e => {
                      e.stopPropagation();
                      prevSlide();
                    }}
                  />
                  <ArrowCircle
                    onClick={e => {
                      e.stopPropagation();
                      nextSlide();
                    }}
                  />
                </div>
              )}
              {!isMobile ? (
                <div
                  ref={sliderRef}
                  className='keen-slider easy-atmosphere__gallery-slider'
                >
                  {barInteriorPhotos.map(photo => (
                    <div
                      key={photo.id}
                      className='keen-slider__slide easy-atmosphere__gallery-slide'
                    >
                      <Picture icon={photo.image} alt={photo.alt} />
                    </div>
                  ))}
                </div>
              ) : (
                <div className='easy-atmosphere__gallery-scroll'>
                  {barInteriorPhotos.map(photo => (
                    <div
                      key={photo.id}
                      className='easy-atmosphere__gallery-slide'
                    >
                      <Picture icon={photo.image} alt={photo.alt} />
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className='easy-atmosphere__gallery-controls'>
              <div className='easy-atmosphere__gallery-caption'>
                {barInteriorPhotos[currentSlide]?.description ||
                  (shopInfo?.id
                    ? shopsData.find(shop => shop.id === shopInfo.id)?.title
                    : null) ||
                  'Фото изнутри наших баров'}
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }
);

export default EasyAtmosphere;
